import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Shield, Server, Globe, Database, Cloud, ArrowLeft, Wifi, Lock, Users, FileCheck, RefreshCw, Play, AlertTriangle, CheckCircle, XCircle, Info, X } from 'lucide-react';
import { apiService, GoAgent } from '../services/api';
import { deviceMatchesOS } from '../utils/devicePlatformMatch';
import { isLastSeenOnline, getOnlineGraceMs, loadOnlineGrace } from '../utils/agentOnline';
import { HubPage } from './ui/HubShell';
import './ComplianceHub.css';

/** Map compliance category to platform filter for server/device list */
function getPlatformForCategory(categoryId: InfrastructureCardId | null): string | null {
  if (!categoryId) return null;
  if (categoryId === 'linux-hardening') return 'plat-linux';
  if (categoryId === 'windows-server') return 'plat-windows';
  // web-tier, database, cloud-assets: show all (multi-OS)
  return null;
}

type InfrastructureCardId = 'linux-hardening' | 'windows-server' | 'web-tier' | 'database' | 'cloud-assets';
type DriftStatus = 'in-spec' | 'drift' | 'critical';
type CheckType = 'network' | 'security' | 'users' | 'file-integrity' | 'authentication' | 'access-control' | 'encryption' | 'audit' | 'backup' | 'iam' | 'logging';

interface InfrastructureCard {
  id: InfrastructureCardId;
  name: string;
  icon: React.ElementType;
  healthPct: number;
  driftStatus: DriftStatus;
  nonCompliantCount: number;
  description: string;
  totalRules: number;
  passCount: number;
  warnCount: number;
  failCount: number;
}

interface ComplianceRule {
  rule_id: string;
  title: string;
  description: string;
  check_type: CheckType;
  severity: string;
  platform: string;
  benchmark_ref: string;
  expected_value: string;
  remediation_steps: string;
  auto_remediate: boolean;
  result_summary?: {
    status: 'pass' | 'warn' | 'fail' | 'not_evaluated';
    pass_count: number;
    warn_count: number;
    fail_count: number;
    total_evaluated: number;
  };
}

interface EvidenceModalData {
  rule: ComplianceRule;
  deviceId?: string;
  evidence?: Record<string, unknown>;
  actualValue?: string;
}

// Icon mapping
const ICON_MAP: Record<string, React.ElementType> = {
  'Server': Server,
  'Shield': Shield,
  'Globe': Globe,
  'Database': Database,
  'Cloud': Cloud,
};

const CHECK_TYPE_ICONS: Record<string, React.ElementType> = {
  'network': Wifi,
  'security': Lock,
  'users': Users,
  'file-integrity': FileCheck,
  'authentication': Lock,
  'access-control': Users,
  'encryption': Shield,
  'audit': FileCheck,
  'backup': Database,
  'iam': Users,
  'logging': FileCheck,
};

function getHealthClass(pct: number): 'compliant' | 'warning' | 'critical' {
  if (pct >= 90) return 'compliant';
  if (pct >= 70) return 'warning';
  return 'critical';
}

function getDriftStatus(healthPct: number, failCount: number): DriftStatus {
  if (healthPct >= 90 && failCount === 0) return 'in-spec';
  if (healthPct < 70 || failCount > 5) return 'critical';
  return 'drift';
}

function getGoAgentStatus(agent: GoAgent): 'online' | 'offline' | 'warning' {
  if (!agent.last_seen) return 'offline';
  if (isLastSeenOnline(agent.last_seen)) return 'online';
  const age = Date.now() - new Date(agent.last_seen).getTime();
  if (age < Math.max(getOnlineGraceMs() * 3, 30 * 60 * 1000)) return 'warning';
  return 'offline';
}

function getRuleStatusIcon(status: string) {
  switch (status) {
    case 'pass':
      return <CheckCircle size={14} className="ch-rule-icon pass" />;
    case 'fail':
      return <XCircle size={14} className="ch-rule-icon fail" />;
    case 'warn':
      return <AlertTriangle size={14} className="ch-rule-icon warn" />;
    default:
      return <Info size={14} className="ch-rule-icon pending" />;
  }
}

type ComplianceHubProps = {
  /** When true, skip outer HubPage (parent already provides title/tabs). */
  embedded?: boolean;
};

const ComplianceHub: React.FC<ComplianceHubProps> = ({ embedded = false }) => {
  const [selectedCard, setSelectedCard] = useState<InfrastructureCardId | null>(null);
  const [selectedCheckType, setSelectedCheckType] = useState<CheckType | null>(null);
  const [cards, setCards] = useState<InfrastructureCard[]>([]);
  const [cardsLoading, setCardsLoading] = useState(true);
  const [rules, setRules] = useState<ComplianceRule[]>([]);
  const [rulesByCheckType, setRulesByCheckType] = useState<Record<string, ComplianceRule[]>>({});
  const [rulesLoading, setRulesLoading] = useState(false);
  const [agents, setAgents] = useState<GoAgent[]>([]);
  const [agentsLoading, setAgentsLoading] = useState(false);
  const [evidenceModal, setEvidenceModal] = useState<EvidenceModalData | null>(null);
  const [remediationInProgress, setRemediationInProgress] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

  // Load category summary for infrastructure cards
  const loadCategorySummary = useCallback(async () => {
    setCardsLoading(true);
    try {
      const response = await apiService.getComplianceRuleCategorySummary();
      if (response.success && response.data?.categories) {
        const loadedCards: InfrastructureCard[] = response.data.categories.map((cat: {
          id: string;
          name: string;
          description: string;
          icon: string;
          total_rules: number;
          pass_count: number;
          warn_count: number;
          fail_count: number;
          non_compliant_count: number;
          health_pct: number;
          drift_status: string;
        }) => ({
          id: cat.id as InfrastructureCardId,
          name: cat.name,
          description: cat.description,
          icon: ICON_MAP[cat.icon] || Shield,
          totalRules: cat.total_rules,
          passCount: cat.pass_count,
          warnCount: cat.warn_count,
          failCount: cat.fail_count,
          nonCompliantCount: cat.non_compliant_count,
          healthPct: cat.health_pct || (cat.total_rules > 0 ? 0 : 100),
          driftStatus: (cat.drift_status as DriftStatus) || getDriftStatus(cat.health_pct, cat.fail_count),
        }));
        setCards(loadedCards);
      }
    } catch (error) {
      console.error('Failed to load category summary:', error);
      // Fallback to empty state
      setCards([]);
    } finally {
      setCardsLoading(false);
      setLastRefresh(new Date());
    }
  }, []);

  // Load rules for selected category. Do NOT include selectedCheckType in deps – it causes a
  // re-render loop: setSelectedCheckType → callback recreated → useEffect runs → setSelectedCheckType(null) → repeat.
  const loadCategoryRules = useCallback(async (categoryId: string) => {
    setRulesLoading(true);
    try {
      const response = await apiService.getComplianceRulesByCategory(categoryId, true);
      if (response.success && response.data) {
        setRules(response.data.rules || []);
        setRulesByCheckType(response.data.rules_by_check_type || {});
        // Auto-select first check type when loading fresh data (selectedCheckType is null at this point)
        const checkTypes = Object.keys(response.data.rules_by_check_type || {});
        if (checkTypes.length > 0) {
          setSelectedCheckType(checkTypes[0] as CheckType);
        }
      }
    } catch (error) {
      console.error('Failed to load category rules:', error);
      setRules([]);
      setRulesByCheckType({});
    } finally {
      setRulesLoading(false);
    }
  }, []);

  // Load Go Agents
  const loadAgents = useCallback(async () => {
    setAgentsLoading(true);
    try {
      const res = await apiService.getGoAgents({ size: 50 });
      setAgents(res.items || []);
    } catch {
      setAgents([]);
    } finally {
      setAgentsLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    loadOnlineGrace();
    loadCategorySummary();
    loadAgents();
  }, [loadCategorySummary, loadAgents]);

  // Load rules when card is selected
  useEffect(() => {
    if (selectedCard) {
      setSelectedCheckType(null);
      loadCategoryRules(selectedCard);
    } else {
      setRules([]);
      setRulesByCheckType({});
      setSelectedCheckType(null);
    }
  }, [selectedCard, loadCategoryRules]);

  // Handle refresh
  const handleRefresh = () => {
    loadCategorySummary();
    if (selectedCard) {
      loadCategoryRules(selectedCard);
    }
  };

  // Handle remediation
  const handleRemediate = async (rule: ComplianceRule, deviceId: string) => {
    if (!rule.auto_remediate) {
      alert('This rule does not support automated remediation. Please follow the manual steps.');
      return;
    }
    
    setRemediationInProgress(rule.rule_id);
    try {
      const response = await apiService.triggerRemediation(rule.rule_id, deviceId);
      if (response.success) {
        alert(`Remediation job created: ${response.data?.job_id}`);
        // Refresh data after remediation
        loadCategoryRules(selectedCard!);
      } else {
        alert('Failed to trigger remediation');
      }
    } catch (error) {
      console.error('Remediation error:', error);
      alert('Failed to trigger remediation');
    } finally {
      setRemediationInProgress(null);
    }
  };

  const selectedCardData = selectedCard ? cards.find((c) => c.id === selectedCard) : null;

  /** Filter agents by selected category's OS (Windows Server → Windows only, Linux Hardening → Linux only) */
  const agentsForCategory = useMemo(() => {
    const platformFilter = getPlatformForCategory(selectedCard);
    if (!platformFilter) return agents;
    return agents.filter((a) => deviceMatchesOS({ platform: a.platform, os: a.os }, platformFilter));
  }, [agents, selectedCard]);
  const checkTypes = Object.keys(rulesByCheckType);
  const currentRules = selectedCheckType ? (rulesByCheckType[selectedCheckType] || []) : rules;

  const refreshActions = (
    <div className="ch-header-actions">
      <span className="ch-last-refresh">
        Last refresh: {lastRefresh.toLocaleTimeString()}
      </span>
      <button
        type="button"
        className="ch-refresh-btn"
        onClick={handleRefresh}
        disabled={cardsLoading}
      >
        <RefreshCw size={14} className={cardsLoading ? 'spinning' : ''} />
        Refresh
      </button>
    </div>
  );

  const body = (
    <div className="compliance-hub">
      {embedded && (
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <p className="text-sm text-slate-600 m-0">
            Server infrastructure compliance. Select a category to view check rules and remediation.
          </p>
          {refreshActions}
        </div>
      )}
      {cardsLoading ? (
        <div className="ch-loading">
          <RefreshCw size={24} className="spinning" />
          <span>Loading compliance data...</span>
        </div>
      ) : cards.length === 0 ? (
        <div className="ch-empty">
          <Shield size={48} />
          <h3>No Compliance Rules Configured</h3>
          <p>Add compliance rules to start monitoring your infrastructure.</p>
        </div>
      ) : (
        <div className="ch-cards-grid">
          {cards.map((card) => {
            const Icon = card.icon;
            const isSelected = selectedCard === card.id;
            const healthClass = getHealthClass(card.healthPct);
            return (
              <div
                key={card.id}
                className={`ch-card ${isSelected ? 'selected' : ''}`}
                onClick={() => setSelectedCard(isSelected ? null : card.id)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && setSelectedCard(selectedCard === card.id ? null : card.id)}
              >
                <div className="ch-card-icon" style={{ background: 'var(--ch-surface)', border: '1px solid var(--ch-border)' }}>
                  <Icon size={22} style={{ color: 'var(--ch-accent)' }} />
                </div>
                <div className="ch-card-title">{card.name}</div>
                <p style={{ fontSize: '0.75rem', color: 'var(--ch-text-muted)', margin: 0 }}>{card.description}</p>
                <div className="ch-card-stats">
                  <span className={`ch-health-pct ${healthClass}`}>
                    {card.totalRules > 0 ? `${Math.round(card.healthPct)}%` : 'N/A'}
                  </span>
                  <span className={`ch-drift-badge ${card.driftStatus}`}>
                    {card.driftStatus === 'in-spec' ? 'In spec' : card.driftStatus}
                  </span>
                  <span className="ch-noncompliant">
                    <span>{card.nonCompliantCount}</span> non-compliant
                  </span>
                </div>
                <div className="ch-card-rule-count">
                  {card.totalRules} rules configured
                </div>
              </div>
            );
          })}
        </div>
      )}

      {selectedCardData && (
        <div className="ch-studio">
          <div className="ch-studio-back">
            <button type="button" onClick={() => setSelectedCard(null)}>
              <ArrowLeft size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
              Back to categories
            </button>
            <span className="ch-studio-title">Studio: {selectedCardData.name}</span>
          </div>

          <div className="ch-studio-left">
            <h3>Check Types</h3>
            {rulesLoading ? (
              <div className="ch-loading-small">Loading...</div>
            ) : checkTypes.length === 0 ? (
              <div className="ch-empty-small">No check types found</div>
            ) : (
              checkTypes.map((ct) => {
                const Icon = CHECK_TYPE_ICONS[ct] || Shield;
                const active = selectedCheckType === ct;
                const typeRules = rulesByCheckType[ct] || [];
                const failCount = typeRules.filter(r => r.result_summary?.status === 'fail').length;
                return (
                  <button
                    key={ct}
                    type="button"
                    className={`ch-category-btn ${active ? 'active' : ''}`}
                    onClick={() => setSelectedCheckType(ct as CheckType)}
                  >
                    <Icon size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />
                    {ct.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    <span className="ch-type-count">({typeRules.length})</span>
                    {failCount > 0 && (
                      <span className="ch-fail-badge">{failCount}</span>
                    )}
                  </button>
                );
              })
            )}
          </div>

          <div className="ch-studio-center">
            <h3>Check Rules {selectedCheckType && `- ${selectedCheckType.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}`}</h3>
            {rulesLoading ? (
              <div className="ch-loading-small">
                <RefreshCw size={16} className="spinning" />
                Loading rules...
              </div>
            ) : currentRules.length === 0 ? (
              <div className="ch-empty-small">
                <Info size={20} />
                <span>No rules in this category</span>
              </div>
            ) : (
              <ul className="ch-check-rules">
                {currentRules.map((rule) => {
                  const status = rule.result_summary?.status || 'not_evaluated';
                  return (
                    <li key={rule.rule_id} className={`ch-check-rule ${status}`}>
                      <div className="ch-rule-header">
                        {getRuleStatusIcon(status)}
                        <span className="ch-rule-title">{rule.title}</span>
                        <span className={`ch-severity-badge ${rule.severity}`}>{rule.severity}</span>
                      </div>
                      <div className="ch-rule-details">
                        <p className="ch-rule-desc">{rule.description}</p>
                        {rule.benchmark_ref && (
                          <span className="ch-benchmark-ref">{rule.benchmark_ref}</span>
                        )}
                        {status === 'fail' && (
                          <div className="ch-rule-actions">
                            <button
                              type="button"
                              className="ch-evidence-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                setEvidenceModal({ rule });
                              }}
                            >
                              <Info size={12} /> Why failed?
                            </button>
                            {rule.auto_remediate && agentsForCategory.length > 0 && (
                              <button
                                type="button"
                                className="ch-remediate-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const onlineAgent = agentsForCategory.find(a => getGoAgentStatus(a) === 'online');
                                  if (onlineAgent) {
                                    handleRemediate(rule, onlineAgent.device_id);
                                  } else {
                                    alert('No online agents available for remediation');
                                  }
                                }}
                                disabled={remediationInProgress === rule.rule_id}
                              >
                                <Play size={12} />
                                {remediationInProgress === rule.rule_id ? 'Running...' : 'Remediate'}
                              </button>
                            )}
                          </div>
                        )}
                        {rule.result_summary && rule.result_summary.total_evaluated > 0 && (
                          <div className="ch-rule-stats">
                            <span className="pass">{rule.result_summary.pass_count} pass</span>
                            <span className="warn">{rule.result_summary.warn_count} warn</span>
                            <span className="fail">{rule.result_summary.fail_count} fail</span>
                          </div>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}

            <div className="ch-servers-section">
              <h4>Servers (Go-Agent status)</h4>
              <table className="ch-server-table">
                <thead>
                  <tr>
                    <th style={{ width: 24 }}></th>
                    <th>Server / Device</th>
                    <th>OS</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {agentsLoading ? (
                    <tr>
                      <td colSpan={4} style={{ color: 'var(--ch-text-muted)', padding: '1rem' }}>Loading agents...</td>
                    </tr>
                  ) : agents.length === 0 ? (
                    <tr>
                      <td colSpan={4} style={{ color: 'var(--ch-text-muted)', padding: '1rem' }}>No Go agents registered.</td>
                    </tr>
                  ) : agentsForCategory.length === 0 ? (
                    <tr>
                      <td colSpan={4} style={{ color: 'var(--ch-text-muted)', padding: '1rem' }}>
                        No {selectedCard === 'windows-server' ? 'Windows' : selectedCard === 'linux-hardening' ? 'Linux' : ''} servers in scope for this category.
                      </td>
                    </tr>
                  ) : (
                    agentsForCategory.slice(0, 12).map((agent) => {
                      const status = getGoAgentStatus(agent);
                      return (
                        <tr key={agent.device_id}>
                          <td>
                            <span className={`ch-go-agent-status ${status}`}>
                              <span className="ch-status-dot" />
                            </span>
                          </td>
                          <td>{agent.name || agent.device_id}</td>
                          <td>{agent.os}</td>
                          <td>
                            <span className={`ch-go-agent-status ${status}`}>
                              <span className="ch-status-dot" />
                              {status === 'online' ? 'Online' : status === 'warning' ? 'Stale' : 'Offline'}
                            </span>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="ch-studio-right">
            <h3>Category Summary</h3>
            <div className="ch-summary-stats">
              <div className="ch-stat-item">
                <span className="ch-stat-value">{selectedCardData.totalRules}</span>
                <span className="ch-stat-label">Total Rules</span>
              </div>
              <div className="ch-stat-item pass">
                <span className="ch-stat-value">{selectedCardData.passCount}</span>
                <span className="ch-stat-label">Passing</span>
              </div>
              <div className="ch-stat-item warn">
                <span className="ch-stat-value">{selectedCardData.warnCount}</span>
                <span className="ch-stat-label">Warnings</span>
              </div>
              <div className="ch-stat-item fail">
                <span className="ch-stat-value">{selectedCardData.failCount}</span>
                <span className="ch-stat-label">Failing</span>
              </div>
            </div>

            <h3>Quick Remediation</h3>
            <div className="ch-quick-remediation">
              <p>Select a failed rule to view remediation options.</p>
              {currentRules.filter(r => r.result_summary?.status === 'fail').length > 0 ? (
                <div className="ch-failed-rules-list">
                  {currentRules
                    .filter(r => r.result_summary?.status === 'fail')
                    .slice(0, 5)
                    .map(rule => (
                      <div key={rule.rule_id} className="ch-failed-rule-item">
                        <XCircle size={12} className="fail" />
                        <span>{rule.title}</span>
                        {rule.auto_remediate && (
                          <span className="ch-auto-badge">Auto</span>
                        )}
                      </div>
                    ))}
                </div>
              ) : (
                <div className="ch-no-failures">
                  <CheckCircle size={20} />
                  <span>No failures detected</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Evidence Modal */}
      {evidenceModal && (
        <div className="ch-modal-overlay" onClick={() => setEvidenceModal(null)}>
          <div className="ch-modal" onClick={e => e.stopPropagation()}>
            <div className="ch-modal-header">
              <h3>Rule Details: {evidenceModal.rule.title}</h3>
              <button type="button" onClick={() => setEvidenceModal(null)}>
                <X size={18} />
              </button>
            </div>
            <div className="ch-modal-body">
              <div className="ch-modal-section">
                <h4>Description</h4>
                <p>{evidenceModal.rule.description}</p>
              </div>
              
              {evidenceModal.rule.benchmark_ref && (
                <div className="ch-modal-section">
                  <h4>Benchmark Reference</h4>
                  <code>{evidenceModal.rule.benchmark_ref}</code>
                </div>
              )}
              
              <div className="ch-modal-section">
                <h4>Expected Value</h4>
                <code>{evidenceModal.rule.expected_value || 'N/A'}</code>
              </div>
              
              {evidenceModal.actualValue && (
                <div className="ch-modal-section">
                  <h4>Actual Value (Evidence)</h4>
                  <code className="fail">{evidenceModal.actualValue}</code>
                </div>
              )}
              
              {evidenceModal.evidence && (
                <div className="ch-modal-section">
                  <h4>Evidence Details</h4>
                  <pre>{JSON.stringify(evidenceModal.evidence, null, 2)}</pre>
                </div>
              )}
              
              <div className="ch-modal-section">
                <h4>Remediation Steps</h4>
                <pre className="ch-remediation-steps">
                  {evidenceModal.rule.remediation_steps || 'No remediation steps provided.'}
                </pre>
              </div>
              
              <div className="ch-modal-section">
                <h4>Severity</h4>
                <span className={`ch-severity-badge large ${evidenceModal.rule.severity}`}>
                  {evidenceModal.rule.severity}
                </span>
              </div>
            </div>
            <div className="ch-modal-footer">
              {evidenceModal.rule.auto_remediate && (
                <button 
                  type="button" 
                  className="ch-btn-primary"
                  onClick={() => {
                    const onlineAgent = agentsForCategory.find(a => getGoAgentStatus(a) === 'online');
                    if (onlineAgent) {
                      handleRemediate(evidenceModal.rule, onlineAgent.device_id);
                      setEvidenceModal(null);
                    } else {
                      alert('No online agents available');
                    }
                  }}
                >
                  <Play size={14} /> Run Remediation
                </button>
              )}
              <button type="button" className="ch-btn-secondary" onClick={() => setEvidenceModal(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  if (embedded) {
    return body;
  }

  return (
    <HubPage
      title="Compliance"
      subtitle="Server infrastructure compliance. Select a category to view check rules and remediation."
      icon={<Shield size={20} />}
      eyebrow="Governance"
      action={refreshActions}
    >
      {body}
    </HubPage>
  );
};

export default ComplianceHub;
